import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, validateToken, authApi } from '../services/api';
import { safeApiCall } from '../utils/apiCaller';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import tokenRefreshService from '../services/tokenRefreshService';
import { getSafeApiUrl } from '../config/environment';

// 사용자 타입 정의
export interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  storeId: number;
  role: string;
  // 추가 정보
  phone?: string;
  businessNumber?: string;
  storeAddress?: string;
  storeName?: string;
  ownerName?: string;
  businessLicenseUrl?: string;
  // 상태 정보
  status?: string;
  approvalStatus?: string;
  rejectionReason?: string;
  approvedAt?: string;
  createdAt?: string;
  // 백엔드 Store 모델과의 호환성을 위한 필드
  passwordHash?: string; // 백엔드 Store 모델의 PasswordHash와 매핑 (보안상 빈 문자열로 전송)
  phoneNumber?: string; // 백엔드 Store 모델의 PhoneNumber와 매핑
  address?: string; // 백엔드 Store 모델의 Address와 매핑
  businessRegistrationNumber?: string; // 백엔드 Store 모델의 BusinessRegistrationNumber와 매핑
  isActive?: boolean; // 백엔드 Store 모델의 IsActive와 매핑
  lastLoginAt?: string; // 백엔드 Store 모델의 LastLoginAt와 매핑
  hasBusinessInfo?: boolean; // 백엔드 Store 모델의 HasBusinessInfo와 매핑
  registrationStep?: number | null; // 백엔드 Store 모델의 RegistrationStep과 매핑 (0: AccountCreated, 1: CardRegistered, 2: InstallationRequested, 3: Completed, null: 기존 사용자)
  registrationSource?: string | null; // "본사" | "외부" (와디즈 등)
}

// 인증 컨텍스트 타입 정의
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithToken: (token: string, user: User) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  updateProfile: (userData: Partial<User>) => Promise<boolean>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
}

// 기본값으로 컨텍스트 생성
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  login: async () => false,
  loginWithToken: async () => false,
  logout: () => {},
  isAuthenticated: false,
  updateProfile: async () => false,
  updatePassword: async (currentPassword: string, newPassword: string) => false,
  setCurrentUser: () => {}
});

// 컨텍스트 프로바이더 프롭스 타입
interface AuthProviderProps {
  children: ReactNode;
}

// 로컬 스토리지에서 유지해야 할 항목들
export const VALID_STORAGE_KEYS = [
  'token',
  'storeId',
  'apiUrl',
  'apiLogging',
  'autoLogin',
  'lowStockHideSettings',
  'expiryHideSettings',
  'adminToken',
  'currentAdmin',
  // 사용자 세션 유지 보강
  'currentUser',
  'storeName',
  'ownerName',
  // 알림 관련 데이터 보호
  'notificationSettings',
  'notificationSubscriptionState',
  'pwaInstalled',
  'pwaInstallDismissed',
  // 기기별 구독 관리 데이터 보호
  'deviceId',
  'fcm_token',
  'fcm_token_timestamp',
  'user_disabled_subscription',
  // 개발 모드 Mock 사용자
  'enableMockUser',
  // 관리자 임시 로그인 토큰
  'impersonate_token',
];

// 로컬 스토리지 정리 함수
export const cleanLocalStorage = () => {
  // 현재 로컬 스토리지 내의 모든 키를 가져옴
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !VALID_STORAGE_KEYS.includes(key)) {
      // 기기별 구독 상태 키는 보호 (subscription_${storeId}_${deviceId} 패턴)
      if (key.startsWith('subscription_')) {
        console.log(`기기별 구독 상태 키 보호: ${key}`);
        continue;
      }
      keysToRemove.push(key);
    }
  }
  
  // 불필요한 키 제거
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`로컬 스토리지에서 제거됨: ${key}`);
  });
  
  console.log('로컬 스토리지 정리 완료 (기기별 구독 상태 보호됨)');
};

// api.ts의 통합 인스턴스 사용 - 토큰 인터셉터 이미 설정됨

// 토큰 유효성 검증은 api.ts의 setupInterceptors에서 처리



// 통합 API 인스턴스 사용 - 인터셉터는 api.ts에서 관리

const getInitialUser = (): User | null => {
  const userStr = localStorage.getItem('currentUser');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {}
  }
  return null;
};

// 인증 컨텍스트 프로바이더 컴포넌트
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(getInitialUser());
  const [loading, setLoading] = useState(true);

  const autoEnableNotifications = async (reason: string) => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return;
      }
      // 로그인 시 자동 활성화 정책: 기존 비활성화 플래그 제거
      localStorage.removeItem('user_disabled_subscription');
      localStorage.removeItem('user_dismissed_renewal');

      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.log('🔕 [자동 알림 활성화] 권한 미허용 - 자동 활성화 중단');
        return;
      }

      const pushNotificationService = (await import('../services/pushNotificationService')).default;
      await pushNotificationService.requestAutoResubscribe(`auto-enable-${reason}`);
      console.log('✅ [자동 알림 활성화] 자동 재구독 완료');
    } catch (error) {
      console.warn('⚠️ [자동 알림 활성화] 실패:', error);
    }
  };

  // 앱 로드 시 로컬 스토리지 정리 및 사용자 세션 확인
  useEffect(() => {
    // 개발 모드에서 Mock 사용자 활성화 체크
    const isDevMode = process.env.NODE_ENV === 'development';
    const enableMockUser = localStorage.getItem('enableMockUser') === 'true';
    
    if (isDevMode && enableMockUser) {
      console.log('🛠️ [개발 모드] Mock 사용자 활성화됨');
      const mockUser: User = {
        id: 1,
        username: 'dev_user',
        name: '개발자',
        email: 'dev@example.com',
        storeId: 1,
        role: 'owner',
        storeName: '테스트 매장',
        ownerName: '개발자',
        status: 'approved',
        approvalStatus: 'approved',
        phone: '010-1234-5678',
        businessNumber: '123-45-67890',
        storeAddress: '서울시 강남구 테스트동 123',
        hasBusinessInfo: true,
        registrationStep: 3,
        registrationSource: '본사'
      };
      
      // Mock 토큰 및 사용자 정보 설정
      localStorage.setItem('token', 'mock_token_for_development');
      localStorage.setItem('storeId', '1');
      localStorage.setItem('currentUser', JSON.stringify(mockUser));
      localStorage.setItem('storeName', mockUser.storeName || '');
      localStorage.setItem('ownerName', mockUser.ownerName || '');
      
      setCurrentUser(mockUser);
      setLoading(false);
      console.log('✅ [개발 모드] Mock 사용자 로그인 완료');
      return;
    }
    
    // 로컬 스토리지 정리
    cleanLocalStorage();
    
    const checkUserSession = async () => {
      try {
        const token = localStorage.getItem('token');
        const storeId = localStorage.getItem('storeId');
        
        // 토큰과 storeId가 모두 있어야 유효한 세션으로 간주
        if (token && storeId) {
          // 클라이언트 측 토큰 유효성 검증
          const isTokenValid = validateToken(token);
          
          try {
            // 토큰이 클라이언트 측에서 유효하지 않더라도 백엔드 검증 시도
            // (클라이언트 시간과 서버 시간 차이 등을 고려)
            const response = await api.get('/stores/me'); // 올바른 엔드포인트 사용
            if (response.data) {
              // 백엔드에서 반환하는 businessLicenseUrl이 상대 경로일 수 있으므로 변환
              let businessLicenseUrl = response.data.businessLicenseUrl;
              if (businessLicenseUrl) {
                const gatewayApiUrl = getSafeApiUrl('GATEWAY');
                // Identity Service 응답 형식: /api/Stores/{id}/documents/{docId}
                // API Gateway 프록시 경로: /api/stores/{storeId}/documents/{documentId}
                if (businessLicenseUrl.startsWith('/api/Stores/')) {
                  const match = businessLicenseUrl.match(/\/api\/Stores\/(\d+)\/documents\/(\d+)/);
                  if (match) {
                    const storeId = match[1];
                    const documentId = match[2];
                    businessLicenseUrl = `${gatewayApiUrl}/stores/${storeId}/documents/${documentId}`;
                  } else {
                    businessLicenseUrl = `${gatewayApiUrl}${businessLicenseUrl.replace('/api/Stores/', '/api/stores/')}`;
                  }
                } else if (businessLicenseUrl.startsWith('/')) {
                  businessLicenseUrl = `${gatewayApiUrl}${businessLicenseUrl}`;
                }
              }
              
              // 로컬에 저장된 보조 정보와 병합(게이트웨이 사용자 모델에 누락된 필드 대비)
              const storedStoreName = localStorage.getItem('storeName') || undefined;
              const storedOwnerName = localStorage.getItem('ownerName') || undefined;
              const mergedUser = {
                ...response.data,
                storeName: response.data.storeName || storedStoreName,
                ownerName: response.data.ownerName || response.data.name || storedOwnerName,
                businessLicenseUrl: businessLicenseUrl || response.data.businessLicenseUrl,
                hasBusinessInfo: response.data.hasBusinessInfo !== undefined ? response.data.hasBusinessInfo : false,
                registrationStep: response.data.registrationStep !== undefined ? response.data.registrationStep : null,
                registrationSource: response.data.registrationSource ?? null
              };
              
              // IsActive가 false인 경우 강제 로그아웃 처리
              if (mergedUser.isActive === false) {
                console.log('세션 복원 실패: 비활성 계정 - 강제 로그아웃 처리');
                cleanLocalStorage();
                setCurrentUser(null);
                return; // 로그아웃 처리 후 early return
              }
              
              // 다중 매장(3.7): 세션 복원 시 localStorage.storeId를 /stores/me 응답으로 덮어쓰지 않음. 전환 후 리로드 시 선택 매장 유지.
              const existingStoreId = localStorage.getItem('storeId');
              const existingStoreName = localStorage.getItem('storeName');
              const parsedStoreId = existingStoreId != null ? parseInt(existingStoreId, 10) : NaN;
              if (!Number.isNaN(parsedStoreId)) {
                mergedUser.storeId = parsedStoreId;
                if (existingStoreName) mergedUser.storeName = existingStoreName;
              } else if (mergedUser.storeId) {
                localStorage.setItem('storeId', mergedUser.storeId.toString());
                if (mergedUser.storeName) localStorage.setItem('storeName', mergedUser.storeName);
              }
              setCurrentUser(mergedUser);
              localStorage.setItem('currentUser', JSON.stringify(mergedUser));
              console.log('세션 복원 성공(병합):', mergedUser);
              if (mergedUser.storeName) localStorage.setItem('storeName', mergedUser.storeName);
              if (mergedUser.ownerName || mergedUser.name) localStorage.setItem('ownerName', (mergedUser.ownerName || mergedUser.name) as string);
              
              // 세션 복원 성공 후 푸시 알림 초기화
              try {
                const pushNotificationService = (await import('../services/pushNotificationService')).default;
                await pushNotificationService.initializeSubscription();
                await pushNotificationService.evaluateTokenRefreshOnAppStart();
                await autoEnableNotifications('session-restore');
                console.log('🔔 [세션 복원] 푸시 알림 초기화 완료');
              } catch (error) {
                console.warn('🔔 [세션 복원] 푸시 알림 초기화 실패:', error);
              }
              
              // 토큰 자동 갱신 서비스 시작
              tokenRefreshService.startAutoRefresh();
              console.log('🔄 [세션 복원] 토큰 자동 갱신 시작');
              
              return; // 성공 시 early return
            }
          } catch (error: any) {
            console.warn('백엔드에서 사용자 정보 조회 실패:', error);
            
            // 401 오류 시에도 토큰을 즉시 삭제하지 않고, 로컬 저장된 정보로 세션 복원 시도
            if (error.response?.status === 401) {
              console.log('401 오류 발생 - 로컬 저장 정보로 세션 복원 시도');
              const storedUser = localStorage.getItem('currentUser');
              if (storedUser) {
                try {
                  const user = JSON.parse(storedUser);
                  setCurrentUser(user);
                  console.log('로컬 저장 정보로 세션 복원 성공:', user);
                  
                  // 로컬 세션 복원 후 푸시 알림 초기화
                  try {
                    const pushNotificationService = (await import('../services/pushNotificationService')).default;
                    await pushNotificationService.initializeSubscription();
                    await pushNotificationService.evaluateTokenRefreshOnAppStart();
                    await autoEnableNotifications('session-restore-local');
                    console.log('🔔 [로컬 세션 복원] 푸시 알림 초기화 완료');
                  } catch (error) {
                    console.warn('🔔 [로컬 세션 복원] 푸시 알림 초기화 실패:', error);
                  }
                  
                  // 토큰 자동 갱신 서비스 시작
                  tokenRefreshService.startAutoRefresh();
                  console.log('🔄 [로컬 세션 복원] 토큰 자동 갱신 시작');
                  
                  return; // 성공 시 early return
                } catch (parseError) {
                  console.error('로컬 사용자 정보 파싱 실패:', parseError);
                }
              }
            }
            
            // 네트워크 오류 등의 경우 토큰 유지
            if (!error.response || error.response.status >= 500) {
              console.log('서버 오류 또는 네트워크 오류 - 토큰 유지');
              const storedUser = localStorage.getItem('currentUser');
              if (storedUser) {
                try {
                  const user = JSON.parse(storedUser);
                  setCurrentUser(user);
                  console.log('네트워크 오류로 인한 로컬 정보 사용:', user);
                  
                  // 네트워크 오류 시 로컬 세션 복원 후 푸시 알림 초기화
                  try {
                    const pushNotificationService = (await import('../services/pushNotificationService')).default;
                    await pushNotificationService.initializeSubscription();
                    await pushNotificationService.evaluateTokenRefreshOnAppStart();
                    await autoEnableNotifications('session-restore-network');
                    console.log('🔔 [네트워크 오류 세션 복원] 푸시 알림 초기화 완료');
                  } catch (error) {
                    console.warn('🔔 [네트워크 오류 세션 복원] 푸시 알림 초기화 실패:', error);
                  }
                  
                  // 토큰 자동 갱신 서비스 시작
                  tokenRefreshService.startAutoRefresh();
                  console.log('🔄 [네트워크 오류 세션 복원] 토큰 자동 갱신 시작');
                  
                  return;
                } catch {}
              }
            }
            
            // 클라이언트 측 토큰 검증이 실패하고 백엔드 검증도 실패한 경우에만 토큰 삭제
            if (!isTokenValid) {
              console.log('클라이언트 및 서버 토큰 검증 모두 실패 - 토큰 삭제');
              setCurrentUser(null);
              localStorage.removeItem('token');
              localStorage.removeItem('currentUser');
              return;
            }
            
            // 기타 오류는 로그만 출력하고 계속 진행
            console.error('사용자 정보 로드 오류:', error);
          }
        } else {
          // 토큰이나 storeId가 없으면 사용자 정보 초기화
          console.log('토큰 또는 storeId가 없습니다.');
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('세션 확인 중 오류 발생:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUserSession();
  }, []);

  // 로그인 함수
  const login = async (email: string, password: string) => {
    try {
      console.log('로그인 시도:', email);
      
      // 로컬 스토리지 정리 (이전 사용자 데이터 제거)
      cleanLocalStorage();
      
      // 백엔드 API 연결 및 로그인 요청 (Identity 서비스 경로 사용)
      // authApi.login은 이미 email을 받아서 내부적으로 username으로 변환하여 전송
      const loginResult = await authApi.login(email, password);
      console.log('로그인 응답:', loginResult);

      // 응답 확인
      if (loginResult && loginResult.token) {
        // authApi.login에서 토큰/스토어ID 저장을 처리함. 여기서는 사용자 정보만 설정
        if (loginResult.user) {
          // hasBusinessInfo 필드가 없으면 false로 기본값 설정
          // registrationStep 필드도 포함
          const userWithBusinessInfo = {
            ...loginResult.user,
            hasBusinessInfo: loginResult.user.hasBusinessInfo !== undefined ? loginResult.user.hasBusinessInfo : false,
            registrationStep: loginResult.user.registrationStep !== undefined ? loginResult.user.registrationStep : null,
            registrationSource: loginResult.user.registrationSource ?? null
          };
          setCurrentUser(userWithBusinessInfo);
          localStorage.setItem('currentUser', JSON.stringify(userWithBusinessInfo));
          console.log('AuthContext - 사용자 정보 설정:', userWithBusinessInfo);
          console.log('AuthContext - 사용자 상태:', userWithBusinessInfo.status);
          console.log('AuthContext - 사업자 정보 완료 여부:', userWithBusinessInfo.hasBusinessInfo);
          const sid = userWithBusinessInfo.storeId ?? (userWithBusinessInfo as any).id ?? (userWithBusinessInfo as any).Id;
          if (sid != null) {
            localStorage.setItem('storeId', String(sid));
            console.log('AuthContext - storeId 저장:', sid);
          }
          // 상단바 표기를 위한 보조 저장 (초기 화면 깜빡임/누락 방지)
          if (userWithBusinessInfo.storeName) localStorage.setItem('storeName', userWithBusinessInfo.storeName);
          if (userWithBusinessInfo.ownerName || userWithBusinessInfo.name) {
            localStorage.setItem('ownerName', (userWithBusinessInfo.ownerName || userWithBusinessInfo.name) as string);
          }
        }

        console.log('로그인 성공:', email);
        
        // 로그인 성공 후 푸시 알림 초기화
        try {
          const pushNotificationService = (await import('../services/pushNotificationService')).default;
          await pushNotificationService.initializeSubscription();
          await pushNotificationService.evaluateTokenRefreshOnAppStart();
          await autoEnableNotifications('login');
          console.log('🔔 [로그인] 푸시 알림 초기화 완료');
        } catch (error) {
          console.warn('🔔 [로그인] 푸시 알림 초기화 실패:', error);
        }
        
        // 토큰 자동 갱신 서비스 시작 (로그인 직후 갱신 방지를 위해 약간의 지연 추가)
        setTimeout(() => {
          tokenRefreshService.startAutoRefresh();
          console.log('🔄 [로그인] 토큰 자동 갱신 시작');
        }, 5000); // 5초 후 시작하여 로그인 직후 갱신 시도 방지
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('로그인 중 오류 발생:', error);
      throw error; // 오류를 상위 컴포넌트로 전달
    }
  };

  // 토큰으로 로그인 함수 (관리자 임시 로그인용)
  const loginWithToken = async (token: string, user: User) => {
    try {
      console.log('[IMPERSONATE] 토큰으로 로그인 시도:', user.email || user.username);
      
      // 로컬 스토리지 정리 (이전 사용자 데이터 제거)
      cleanLocalStorage();
      
      // 토큰 저장
      localStorage.setItem('token', token);
      
      // 사용자 정보 설정
      setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
      console.log('[IMPERSONATE] AuthContext - 사용자 정보 설정:', user);
      
      if (user.storeId) {
        localStorage.setItem('storeId', user.storeId.toString());
      }
      
      // 상단바 표기를 위한 보조 저장 (초기 화면 깜빡임/누락 방지)
      if (user.storeName) localStorage.setItem('storeName', user.storeName);
      if (user.ownerName || user.name) {
        localStorage.setItem('ownerName', (user.ownerName || user.name) as string);
      }
      
      // 자동 로그인 활성화
      localStorage.setItem('autoLogin', 'true');
      
      console.log('[IMPERSONATE] 로그인 성공:', user.email || user.username);
      
      // 로그인 성공 후 푸시 알림 초기화
      try {
        const pushNotificationService = (await import('../services/pushNotificationService')).default;
        await pushNotificationService.initializeSubscription();
        await pushNotificationService.evaluateTokenRefreshOnAppStart();
        await autoEnableNotifications('impersonate');
        console.log('🔔 [IMPERSONATE] 푸시 알림 초기화 완료');
      } catch (error) {
        console.warn('🔔 [IMPERSONATE] 푸시 알림 초기화 실패:', error);
      }
      
      // 토큰 자동 갱신 서비스 시작
      tokenRefreshService.startAutoRefresh();
      console.log('🔄 [IMPERSONATE] 토큰 자동 갱신 시작');
      
      return true;
    } catch (error) {
      console.error('[IMPERSONATE] 로그인 중 오류 발생:', error);
      throw error; // 오류를 상위 컴포넌트로 전달
    }
  };

  // 로그아웃 함수
  const logout = () => {
    // 토큰 자동 갱신 중지
    tokenRefreshService.stopAutoRefresh();
    console.log('🔄 [로그아웃] 토큰 자동 갱신 중지');
    
    // 토큰 삭제
    localStorage.removeItem('token');
    localStorage.removeItem('storeId');
    
    // 자동 로그인 해제
    localStorage.removeItem('autoLogin');
    
    // 로컬 스토리지 정리
    cleanLocalStorage();
    
    // 세션 정리
    setCurrentUser(null);
    console.log('로그아웃 완료');
    
    // 로그아웃 후 로그인 페이지로 리디렉션
    window.location.href = '/login';
  };

  // 프로필 업데이트 함수
  const updateProfile = async (userData: Partial<User>) => {
    try {
      if (!currentUser) {
        console.error('프로필 업데이트 실패: 로그인된 사용자 정보가 없습니다.');
        return false;
      }
      
      // 백엔드에 구현된 올바른 엔드포인트 사용
      const response = await api.put(`/api/users/${currentUser.id}`, userData);
      
      if (response.data) {
        // 사용자 정보 업데이트
        setCurrentUser({ ...currentUser, ...userData });
        console.log('프로필 업데이트 완료:', response.data);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('프로필 업데이트 오류:', error);
      return false;
    }
  };

  // 비밀번호 변경 함수
  const updatePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      if (!currentUser) {
        console.error('비밀번호 변경 실패: 로그인된 사용자 정보가 없습니다.');
        return false;
      }
      
      // 기존 비밀번호 확인
      try {
        const authResponse = await safeApiCall.gateway.post(API_ENDPOINTS.AUTH.STORES_AUTH, {
          username: currentUser.email || currentUser.username, // email을 우선 사용 (username은 email과 동일)
          password: currentPassword
        });
        
        if (!authResponse.data || !authResponse.data.token) {
          console.error('현재 비밀번호가 일치하지 않습니다.');
          return false;
        }
        
        console.log('현재 비밀번호 확인 완료');
      } catch (error) {
        console.error('현재 비밀번호 확인 실패:', error);
        return false;
      }
      
      // 백엔드에 구현된 올바른 엔드포인트 사용
      try {
        // 백엔드에서 기대하는 형식으로 데이터 전송
        const response = await api.put(`/api/users/${currentUser.id}/password`, { 
          newPassword: newPassword,
          currentPassword: currentPassword 
        });
        // 200 (OK) 또는 204 (No Content) 모두 성공으로 처리
        if (response.status === 200 || response.status === 204) {
          console.log('비밀번호가 성공적으로 변경되었습니다.');
          return true;
        }
      } catch (err: any) {
        console.error('비밀번호 변경 실패:', err);
        return false;
      }
      
      return false;
    } catch (error) {
      console.error('비밀번호 변경 중 오류 발생:', error);
      return false;
    }
  };

  // 인증 상태 계산 - 토큰과 currentUser 모두 확인
  const isAuthenticated = !!(currentUser && localStorage.getItem('token') && localStorage.getItem('storeId'));

  const value = {
    currentUser,
    loading,
    login,
    loginWithToken,
    logout,
    isAuthenticated,
    updateProfile,
    updatePassword,
    setCurrentUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 인증 컨텍스트 사용을 위한 훅
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 